import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { CalendarDays, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const TYPE_STYLES = {
  National: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  Festival: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  Exam: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  Vacation: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  Event: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  Other: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};

export const typeBadgeClass = (t) => TYPE_STYLES[t] || TYPE_STYLES.Other;

export const fmtRange = (start, end) => {
  const opt = { day: "numeric", month: "short", year: "numeric" };
  try {
    const s = new Date(start + "T00:00:00").toLocaleDateString(undefined, opt);
    if (!end || end === start) return s;
    const e = new Date(end + "T00:00:00").toLocaleDateString(undefined, opt);
    return `${s} → ${e}`;
  } catch { return start; }
};

export default function HolidaysList({ endpoint = "/holidays" } = {}) {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(endpoint, { params: { year } });
        setHolidays(data.holidays || []);
      } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load holidays"); }
      finally { setLoading(false); }
    })();
  }, [year, endpoint]);

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return holidays.filter((h) => (h.end_date || h.start_date) >= today);
  }, [holidays]);

  if (loading) return <div className="text-slate-500" data-testid="holidays-loading">Loading holidays…</div>;
  if (!holidays.length) return (
    <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="holidays-empty">
      No holidays published for {year} yet.
    </div>
  );

  return (
    <div className="space-y-3" data-testid="holidays-list">
      {holidays.map((h) => {
        const past = (h.end_date || h.start_date) < new Date().toISOString().slice(0, 10);
        return (
          <div
            key={h.id}
            className={`flex items-start gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 ${past ? "opacity-60" : ""}`}
            data-testid={`holiday-row-${h.id}`}
          >
            <div className="h-10 w-10 rounded-xl bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 flex items-center justify-center shrink-0">
              <PartyPopper className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{h.name}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${typeBadgeClass(h.type)}`}>{h.type}</span>
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" /> {fmtRange(h.start_date, h.end_date)}
              </div>
              {h.note && <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{h.note}</div>}
            </div>
          </div>
        );
      })}
      <div className="text-xs text-slate-400 pt-1" data-testid="holidays-upcoming-count">{upcoming.length} upcoming in {year}</div>
    </div>
  );
}
